import { Loading } from "@/components/ui";
import { AUTH_PATHS, WAITLIST_PATH } from "@/constants";
import { FeatureFlag } from "@/constants/featureFlags";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import { Button } from "@paribus/shared-ui";
import { Link } from "react-router-dom";

import type { PricingPlan } from "./Plans";

interface PlanButtonProps {
  plan: PricingPlan;
  isPlanCurrent: boolean;
  hasActiveSubscription: boolean;
  activePlanIsTiered?: boolean;
  activeTierOrder?: number | null;
  isLoading: boolean;
  isLoadingManageSubscription: boolean;
  onPlanSelect?: (
    plan: PricingPlan,
    options?: { action: "subscribe" | "upgrade" | "downgrade" }
  ) => void | Promise<void>;
  onManageSubscription?: () => void | Promise<void>;
  disabled?: boolean;
}

export const PlanButton = ({
  plan,
  isPlanCurrent,
  hasActiveSubscription,
  activePlanIsTiered = false,
  activeTierOrder = null,
  isLoading,
  isLoadingManageSubscription,
  onPlanSelect,
  onManageSubscription,
  disabled = false,
}: PlanButtonProps) => {
  const signupEnabled = useFeatureFlag(FeatureFlag.SignupEnabled);
  const shouldManageSubscription = isPlanCurrent && hasActiveSubscription && onManageSubscription;
  const canUpgradeOrDowngrade = activePlanIsTiered && !isPlanCurrent && activeTierOrder != null;
  // Lower tier_order = costlier plan (high tier) → Upgrade; higher tier_order = cheaper plan (low tier) → Downgrade
  const isUpgrade = canUpgradeOrDowngrade && (plan.tier_order ?? 0) < activeTierOrder;
  const isDowngrade = canUpgradeOrDowngrade && (plan.tier_order ?? 0) > activeTierOrder;

  const buttonText = shouldManageSubscription
    ? "Manage Subscription"
    : isPlanCurrent
      ? "Current Plan"
      : isUpgrade
        ? "Upgrade"
        : isDowngrade
          ? "Downgrade"
          : "Subscribe";

  const action: "subscribe" | "upgrade" | "downgrade" = isUpgrade
    ? "upgrade"
    : isDowngrade
      ? "downgrade"
      : "subscribe";

  const handleClick = async () => {
    if (shouldManageSubscription) {
      await onManageSubscription?.();
      return;
    }
    if (onPlanSelect) {
      await onPlanSelect(plan, { action });
    }
  };

  if (!onPlanSelect && !onManageSubscription) {
    const ctaPath = signupEnabled ? AUTH_PATHS.SIGNUP : WAITLIST_PATH;
    const ctaLabel = signupEnabled ? "Get Started" : "Join waitlist";
    return (
      <Button
        asChild
        className="mb-6 mt-6 w-full rounded-lg bg-primary py-6 font-medium text-white hover:bg-primary-hover"
      >
        <Link to={ctaPath}>{ctaLabel}</Link>
      </Button>
    );
  }

  return (
    <Button
      onClick={handleClick}
      disabled={
        disabled ||
        isLoading ||
        isLoadingManageSubscription ||
        (hasActiveSubscription && !isPlanCurrent && !activePlanIsTiered)
      }
      className="mb-6 mt-6 w-full rounded-lg border-0 bg-primary py-6 font-medium text-white transition-all hover:bg-primary hover:text-white hover:shadow-lg"
    >
      {isLoading || (isPlanCurrent && isLoadingManageSubscription) ? (
        <>
          <Loading size={16} tone="inverse" fullScreen={false} />
          Processing...
        </>
      ) : (
        buttonText
      )}
    </Button>
  );
};
